import { Formik, Form, ErrorMessage } from 'formik';
import * as yup from 'yup';
import { $t } from 'hooks/i18n';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataSource } from 'components/DataSource';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { PageRequestError } from 'components/PageRequestError';
import { ProgressSpinner } from 'primereact/progressspinner';
import { RadioButton } from 'primereact/radiobutton';
import { Title } from 'components/Title';
import useApp from 'hooks/useApp';
import useAuth from 'hooks/useAuth';

import useEditPage from 'hooks/useEditPage';
const GrouploancontractEditPage = (props) => {
		const auth = useAuth();
	const app = useApp();
	// form validation schema
	const validationSchema = yup.object().shape({
		loanaccountno: yup.string().required().label($t('loanACNo')),
		groupid: yup.string().required().label($t('group')),
		memberid: yup.string().required().label($t('memberid')),
		branchcode: yup.string().required().label($t('branch')),
		loanproductid: yup.string().required().label($t('loanProduct')),
		chk_ffirstemi: yup.string().required().label($t('chkFfirstemi')),
		first_emi_date: yup.string().nullable().label($t('firstEmiDate')),
		loanamount: yup.number().required().label($t('loanAmount')),
		loan_type: yup.string().required().label($t('loanType')),
		interestrate: yup.number().required().label($t('interestRate')),
		loanterm: yup.number().required().label($t('loanTerm')),
		loanpaymentfrequency: yup.string().required().label($t('frequency')),
		processingfee: yup.string().nullable().label($t('processingFee')),
		insurancefee: yup.string().nullable().label($t('insurancefee')),
		cgst: yup.string().required().label($t('cgst')),
		igst: yup.string().required().label($t('igst')),
		sgst: yup.string().required().label($t('sgst')),
		datecontractstarts: yup.string().required().label($t('startDate')),
		amort_type: yup.string().required().label($t('amortType')),
		loanpaymentamountdue: yup.number().nullable().label($t('emi')),
		datecontractends: yup.string().nullable().label($t('endDate')),
		loanpaymentduedate: yup.string().required().label($t('emiDate')),
		disbursementdate: yup.string().required().label($t('disbursementDate')),
		amountdisbursed: yup.number().required().label($t('amountDisbursed')),
		chequeno: yup.string().nullable().label($t('chequeno')),
		ac_code: yup.string().required().label($t('accountCode')),
		policy_number_bor: yup.string().nullable().label($t('policyNumberBor')),
		policy_number_co: yup.string().nullable().label($t('policyNumberCo')),
		name_insurance: yup.string().nullable().label($t('nameInsurance')),
		insu_start_bor: yup.string().nullable().label($t('insuStartBor')),
		insu_end_bor: yup.string().nullable().label($t('insuEndBor')),
		insu_start_co: yup.string().nullable().label($t('insuStartCo')),
		insu_end_co: yup.string().nullable().label($t('insuEndCo')),
		ac_closed: yup.number().required().label($t('acClosed')),
		sec_type: yup.number().required().label($t('secType')),
		empcode_sale: yup.string().nullable().label($t('employeeSales')),
		empcode_collection: yup.string().nullable().label($t('employeeCollection')),
		unitid: yup.string().nullable().label($t('unitid')),
		app_rejected: yup.number().nullable().label($t('appRejected')),
		rejection_reason: yup.string().nullable().label($t('rejectionReason')),
		appove_roleid: yup.number().nullable().label($t('appoveRoleid')),
		date_closed: yup.string().nullable().label($t('dateClosed')),
		userid: yup.number().nullable().label($t('userid'))
	});
	// form default values
	const formDefaultValues = {
		loanaccountno: '', 
		groupid: '', 
		memberid: '', 
		branchcode: '', 
		loanproductid: '', 
		chk_ffirstemi: '', 
		first_emi_date: new Date(), 
		loanamount: '', 
		loan_type: '', 
		interestrate: '', 
		loanterm: '', 
		loanpaymentfrequency: '', 
		processingfee: "0", 
		insurancefee: "0", 
		cgst: "((0))", 
		igst: "((0))", 
		sgst: "((0))", 
		datecontractstarts: new Date(), 
		amort_type: '', 
		loanpaymentamountdue: '', 
		datecontractends: new Date(), 
		loanpaymentduedate: new Date(), 
		disbursementdate: new Date(), 
		amountdisbursed: "((0))", 
		chequeno: '', 
		ac_code: '', 
		policy_number_bor: '', 
		policy_number_co: '', 
		name_insurance: '', 
		insu_start_bor: new Date(), 
		insu_end_bor: new Date(), 
		insu_start_co: new Date(), 
		insu_end_co: new Date(), 
		ac_closed: "((0))", 
		sec_type: "((0))", 
		empcode_sale: '', 
		empcode_collection: '', 
		unitid: '', 
		app_rejected: '', 
		rejection_reason: '', 
		appove_roleid: '', 
		date_closed: new Date(), 
		userid: auth.userId, 
	}
	//where page logics resides
	const pageController = useEditPage({ props, formDefaultValues, afterSubmit });
	//destructure and grab what we need
	const { formData, handleSubmit, submitForm, pageReady, loading, saving, apiRequestError, inputClassName } = pageController
	//Event raised on form submit success
	function afterSubmit(response){
		app.flashMsg(props.msgTitle, props.msgAfterSave);
		if(app.isDialogOpen()){
			app.closeDialogs(); // if page is open as dialog, close dialog
		}
		else if(props.redirect) {
			app.navigate(`/grouploancontract`);
		}
	}
	// loading form data from api
	if(loading){
		return (
			<div className="p-3 text-center">
				<ProgressSpinner style={{width:'50px', height:'50px'}} />
			</div>
		);
	}
	//display error page 
	if(apiRequestError){
		return (
			<PageRequestError error={apiRequestError} />
		);
	}
	//page is ready when formdata loaded successfully
	if(pageReady){
		return (
<main id="GrouploancontractEditPage" className="main-page">
    { (props.showHeader) && 
    <section className="page-section mb-3" >
        <div className="container">
            <div className="grid justify-content-between align-items-center">
                { !props.isSubPage && 
                <div className="col-fixed " >
                    <Button onClick={() => app.navigate(-1)} label={$t('')}  className="p-button p-button-text " icon="pi pi-arrow-left"  />
                </div>
                }
                <div className="col " >
                    <Title title={$t('editGrouploancontract')}   titleClass="text-2xl text-primary font-bold" subTitleClass="text-500"      separator={false} />
                </div>
            </div>
        </div>
    </section>
    }
    <section className="page-section " >
        <div className="container">
            <div className="grid ">
                <div className="md:col-9 sm:col-12 comp-grid" >
                    <div >
                        <Formik
                            initialValues={formData}
                            validationSchema={validationSchema} 
                            onSubmit={(values, actions) => {
                            submitForm(values);
                            }
                            }
                            >
                            { (formik) => {
                            return (
                            <Form className={`${!props.isSubPage ? 'card  ' : ''}`}>
                                <div className="grid">
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('loanACNo')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="loanaccountno"  onChange={formik.handleChange}  value={formik.values.loanaccountno}   label={$t('loanACNo')} type="text" placeholder={$t('enterLoanACNo')}     disabled   className={inputClassName(formik?.errors?.loanaccountno)} />
                                                <ErrorMessage name="loanaccountno" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('group')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <DataSource   apiPath="components_data/groupid_option_list"  >
                                                    {
                                                    ({ response }) => 
                                                    <>
                                                    <Dropdown  name="groupid"     optionLabel="label" optionValue="value" value={formik.values.groupid} onChange={formik.handleChange} options={response} label={$t('group')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.groupid)}   />
                                                    <ErrorMessage name="groupid" component="span" className="p-error" />
                                                    </>
                                                    }
                                                </DataSource>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('memberid')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <DataSource onLoad={(res) => formik.setFieldValue('memberid', '')} loadOnMount={false} apiPath={`components_data/memberid_option_list_2?lookup_groupid=${formik.values.groupid}`}  >
                                                    {
                                                    ({ response }) => 
                                                    <>
                                                    <Dropdown  name="memberid"     optionLabel="label" optionValue="value" value={formik.values.memberid} onChange={formik.handleChange} options={response} label={$t('memberid')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.memberid)}   />
                                                    <ErrorMessage name="memberid" component="span" className="p-error" />
                                                    </>
                                                    }
                                                </DataSource>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('branch')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <DataSource   apiPath="components_data/branchcode_option_list"  >
                                                    {
                                                    ({ response }) => 
                                                    <>
                                                    <Dropdown  name="branchcode"     optionLabel="label" optionValue="value" value={formik.values.branchcode} onChange={formik.handleChange} options={response} label={$t('branch')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.branchcode)}   />
                                                    <ErrorMessage name="branchcode" component="span" className="p-error" />
                                                    </>
                                                    }
                                                </DataSource>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('loanProduct')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <DataSource   apiPath="components_data/loanproductid_option_list"  >
                                                    {
                                                    ({ response }) => 
                                                    <>
                                                    <Dropdown  name="loanproductid"     optionLabel="label" optionValue="value" value={formik.values.loanproductid} onChange={formik.handleChange} options={response} label={$t('loanProduct')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.loanproductid)}   />
                                                    <ErrorMessage name="loanproductid" component="span" className="p-error" />
                                                    </>
                                                    }
                                                </DataSource>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('chkFfirstemi')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <div className="flex flex-wrap">
                                                    {
                                                    app.menus.chkFfirstemi.map((option) => {
                                                    return (
                                                    <div key={option.value} className="field-radiobutton  mx-3">
                                                        <RadioButton inputId={option.value} name="chk_ffirstemi" value={option.value} onChange={formik.handleChange}  checked={formik.values.chk_ffirstemi === option.value} className={inputClassName(formik?.errors?.chk_ffirstemi, '')} />
                                                        <label htmlFor={option.value}>{option.label}</label>
                                                    </div>
                                                    )
                                                    })
                                                    }
                                                </div>
                                                <ErrorMessage name="chk_ffirstemi" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('firstEmiDate')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="first_emi_date" showButtonBar className={inputClassName(formik?.errors?.first_emi_date)} dateFormat="yy-mm-dd" value={formik.values.first_emi_date} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="first_emi_date" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('loanAmount')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="loanamount"  onChange={formik.handleChange}  value={formik.values.loanamount}   label={$t('loanAmount')} type="number" placeholder={$t('enterLoanAmount')}  min={0}  step={0.1}    className={inputClassName(formik?.errors?.loanamount)} />
                                                <ErrorMessage name="loanamount" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('loanType')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Dropdown  name="loan_type"     optionLabel="label" optionValue="value" value={formik.values.loan_type} onChange={formik.handleChange} options={app.menus.loanType} label={$t('loanType')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.loan_type)}   />
                                                <ErrorMessage name="loan_type" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('interestRate')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="interestrate"  onChange={formik.handleChange}  value={formik.values.interestrate}   label={$t('interestRate')} type="number" placeholder={$t('enterInterestRate')}  min={0}  step={0.1}    className={inputClassName(formik?.errors?.interestrate)} />
                                                <ErrorMessage name="interestrate" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('loanTerm')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="loanterm"  onChange={formik.handleChange}  value={formik.values.loanterm}   label={$t('loanTerm')} type="number" placeholder={$t('enterLoanTerm')}  min={0}  step="any" disabled   className={inputClassName(formik?.errors?.loanterm)} />
                                                <ErrorMessage name="loanterm" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('frequency')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="loanpaymentfrequency"  onChange={formik.handleChange}  value={formik.values.loanpaymentfrequency}   label={$t('frequency')} type="text" placeholder={$t('enterFrequency')}     disabled   className={inputClassName(formik?.errors?.loanpaymentfrequency)} />
                                                <ErrorMessage name="loanpaymentfrequency" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('processingFee')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="processingfee"  onChange={formik.handleChange}  value={formik.values.processingfee}   label={$t('processingFee')} type="text" placeholder={$t('enterProcessingFee')}     disabled   className={inputClassName(formik?.errors?.processingfee)} />
                                                <ErrorMessage name="processingfee" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('insurancefee')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="insurancefee"  onChange={formik.handleChange}  value={formik.values.insurancefee}   label={$t('insurancefee')} type="text" placeholder={$t('enterInsurancefee')}     disabled   className={inputClassName(formik?.errors?.insurancefee)} />
                                                <ErrorMessage name="insurancefee" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('cgst')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="cgst"  onChange={formik.handleChange}  value={formik.values.cgst}   label={$t('cgst')} type="text" placeholder={$t('enterCgst')}     disabled   className={inputClassName(formik?.errors?.cgst)} />
                                                <ErrorMessage name="cgst" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('igst')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="igst"  onChange={formik.handleChange}  value={formik.values.igst}   label={$t('igst')} type="text" placeholder={$t('enterIgst')}     disabled   className={inputClassName(formik?.errors?.igst)} />
                                                <ErrorMessage name="igst" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('sgst')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="sgst"  onChange={formik.handleChange}  value={formik.values.sgst}   label={$t('sgst')} type="text" placeholder={$t('enterSgst')}     disabled   className={inputClassName(formik?.errors?.sgst)} />
                                                <ErrorMessage name="sgst" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('startDate')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="datecontractstarts" showButtonBar className={inputClassName(formik?.errors?.datecontractstarts)} dateFormat="yy-mm-dd" value={formik.values.datecontractstarts} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="datecontractstarts" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('amortType')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Dropdown  name="amort_type"     optionLabel="label" optionValue="value" value={formik.values.amort_type} onChange={formik.handleChange} options={app.menus.amortType} label={$t('amortType')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.amort_type)}   />
                                                <ErrorMessage name="amort_type" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('emi')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="loanpaymentamountdue"  onChange={formik.handleChange}  value={formik.values.loanpaymentamountdue}   label={$t('emi')} type="number" placeholder={$t('enterEmi')}  min={0}  step={0.1} disabled   className={inputClassName(formik?.errors?.loanpaymentamountdue)} />
                                                <ErrorMessage name="loanpaymentamountdue" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('endDate')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="datecontractends" showButtonBar className={inputClassName(formik?.errors?.datecontractends)} dateFormat="yy-mm-dd" value={formik.values.datecontractends} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="datecontractends" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('emiDate')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="loanpaymentduedate" showButtonBar className={inputClassName(formik?.errors?.loanpaymentduedate)} dateFormat="yy-mm-dd" value={formik.values.loanpaymentduedate} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="loanpaymentduedate" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('disbursementDate')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="disbursementdate" showButtonBar className={inputClassName(formik?.errors?.disbursementdate)} dateFormat="yy-mm-dd" value={formik.values.disbursementdate} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="disbursementdate" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('amountDisbursed')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="amountdisbursed"  onChange={formik.handleChange}  value={formik.values.amountdisbursed}   label={$t('amountDisbursed')} type="number" placeholder={$t('enterAmountDisbursed')}  min={0}  step={0.1} disabled   className={inputClassName(formik?.errors?.amountdisbursed)} />
                                                <ErrorMessage name="amountdisbursed" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('chequeno')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="chequeno"  onChange={formik.handleChange}  value={formik.values.chequeno}   label={$t('chequeno')} type="text" placeholder={$t('enterChequeno')}        className={inputClassName(formik?.errors?.chequeno)} />
                                                <ErrorMessage name="chequeno" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('accountCode')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <DataSource   apiPath="components_data/ac_code_option_list"  >
                                                    {
                                                    ({ response }) => 
                                                    <>
                                                    <Dropdown  name="ac_code"     optionLabel="label" optionValue="value" value={formik.values.ac_code} onChange={formik.handleChange} options={response} label={$t('accountCode')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.ac_code)}   />
                                                    <ErrorMessage name="ac_code" component="span" className="p-error" />
                                                    </>
                                                    }
                                                </DataSource>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('policyNumberBor')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="policy_number_bor"  onChange={formik.handleChange}  value={formik.values.policy_number_bor}   label={$t('policyNumberBor')} type="text" placeholder={$t('enterPolicyNumberBor')}        className={inputClassName(formik?.errors?.policy_number_bor)} />
                                                <ErrorMessage name="policy_number_bor" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('policyNumberCo')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="policy_number_co"  onChange={formik.handleChange}  value={formik.values.policy_number_co}   label={$t('policyNumberCo')} type="text" placeholder={$t('enterPolicyNumberCo')}        className={inputClassName(formik?.errors?.policy_number_co)} />
                                                <ErrorMessage name="policy_number_co" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('nameInsurance')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="name_insurance"  onChange={formik.handleChange}  value={formik.values.name_insurance}   label={$t('nameInsurance')} type="text" placeholder={$t('enterNameInsurance')}        className={inputClassName(formik?.errors?.name_insurance)} />
                                                <ErrorMessage name="name_insurance" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('insuStartBor')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="insu_start_bor" showButtonBar className={inputClassName(formik?.errors?.insu_start_bor)} dateFormat="yy-mm-dd" value={formik.values.insu_start_bor} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="insu_start_bor" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('insuEndBor')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="insu_end_bor" showButtonBar className={inputClassName(formik?.errors?.insu_end_bor)} dateFormat="yy-mm-dd" value={formik.values.insu_end_bor} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="insu_end_bor" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('insuStartCo')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="insu_start_co" showButtonBar className={inputClassName(formik?.errors?.insu_start_co)} dateFormat="yy-mm-dd" value={formik.values.insu_start_co} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="insu_start_co" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('insuEndCo')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="insu_end_co" showButtonBar className={inputClassName(formik?.errors?.insu_end_co)} dateFormat="yy-mm-dd" value={formik.values.insu_end_co} onChange={formik.handleChange} showIcon        />
                                                <ErrorMessage name="insu_end_co" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('acClosed')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="ac_closed"  onChange={formik.handleChange}  value={formik.values.ac_closed}   label={$t('acClosed')} type="number" placeholder={$t('enterAcClosed')}  min={0}  step="any"    className={inputClassName(formik?.errors?.ac_closed)} />
                                                <ErrorMessage name="ac_closed" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('secType')} *
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="sec_type"  onChange={formik.handleChange}  value={formik.values.sec_type}   label={$t('secType')} type="number" placeholder={$t('enterSecType')}  min={0}  step="any"    className={inputClassName(formik?.errors?.sec_type)} />
                                                <ErrorMessage name="sec_type" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('employeeSales')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <DataSource   apiPath="components_data/empcode_sale_option_list"  >
                                                    {
                                                    ({ response }) => 
                                                    <>
                                                    <Dropdown  name="empcode_sale"     optionLabel="label" optionValue="value" value={formik.values.empcode_sale} onChange={formik.handleChange} options={response} label={$t('employeeSales')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.empcode_sale)}   />
                                                    <ErrorMessage name="empcode_sale" component="span" className="p-error" />
                                                    </>
                                                    }
                                                </DataSource>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('employeeCollection')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <DataSource   apiPath="components_data/empcode_sale_option_list"  >
                                                    {
                                                    ({ response }) => 
                                                    <>
                                                    <Dropdown  name="empcode_collection"     optionLabel="label" optionValue="value" value={formik.values.empcode_collection} onChange={formik.handleChange} options={response} label={$t('employeeCollection')}  placeholder={$t('selectAValue')}  className={inputClassName(formik?.errors?.empcode_collection)}   />
                                                    <ErrorMessage name="empcode_collection" component="span" className="p-error" />
                                                    </>
                                                    }
                                                </DataSource>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('unitid')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="unitid"  onChange={formik.handleChange}  value={formik.values.unitid}   label={$t('unitid')} type="text" placeholder={$t('enterUnitid')}        className={inputClassName(formik?.errors?.unitid)} />
                                                <ErrorMessage name="unitid" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('appRejected')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="app_rejected"  onChange={formik.handleChange}  value={formik.values.app_rejected}   label={$t('appRejected')} type="number" placeholder={$t('enterAppRejected')}  min={0}  step="any"    className={inputClassName(formik?.errors?.app_rejected)} />
                                                <ErrorMessage name="app_rejected" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('rejectionReason')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="rejection_reason"  onChange={formik.handleChange}  value={formik.values.rejection_reason}   label={$t('rejectionReason')} type="text" placeholder={$t('enterRejectionReason')}        className={inputClassName(formik?.errors?.rejection_reason)} />
                                                <ErrorMessage name="rejection_reason" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('appoveRoleid')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="appove_roleid"  onChange={formik.handleChange}  value={formik.values.appove_roleid}   label={$t('appoveRoleid')} type="number" placeholder={$t('enterAppoveRoleid')}  min={0}  step="any"    className={inputClassName(formik?.errors?.appove_roleid)} />
                                                <ErrorMessage name="appove_roleid" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('dateClosed')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <Calendar name="date_closed" value={formik.values.date_closed} onChange={formik.handleChange} showButtonBar showTime dateFormat="yy-mm-dd" hourFormat="24"showIcon className={inputClassName(formik?.errors?.date_closed)}        />
                                                <ErrorMessage name="date_closed" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-4 col-12">
                                        <div className="formgrid grid">
                                            <div className="col-12 md:col-3">
                                                {$t('userid')} 
                                            </div>
                                            <div className="col-12 md:col-9">
                                                <InputText name="userid"  onChange={formik.handleChange}  value={formik.values.userid}   label={$t('userid')} type="number" placeholder={$t('enterUserid')}  min={0}  step="any" disabled   className={inputClassName(formik?.errors?.userid)} />
                                                <ErrorMessage name="userid" component="span" className="p-error" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                { props.showFooter && 
                                <div className="text-center my-3">
                                    <Button onClick={(e) => handleSubmit(e, formik)}  type="submit" label={$t('update')} icon="pi pi-send" loading={saving} />
                                </div>
                                }
                            </Form>
                            );
                            }
                            }
                            </Formik>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </main>
		);
	}
}
GrouploancontractEditPage.defaultProps = {
	primaryKey: 'loanaccountno',
	pageName: 'grouploancontract',
	apiPath: 'grouploancontract/edit',
	routeName: 'grouploancontractedit',
	submitButtonLabel: $t('update'),
	formValidationError: $t('formIsInvalid'),
	formValidationMsg: $t('pleaseCompleteTheForm'),
	msgTitle: $t('updateRecord'),
	msgAfterSave: $t('recordUpdatedSuccessfully'),
	msgBeforeSave: $t(''),
	showHeader: true,
	showFooter: true,
	redirect: true,
	isSubPage: false
}
export default GrouploancontractEditPage;
